import React, { useEffect, useState } from "react";
import { Table, Button } from "antd";
import { companyID } from "../../../auth";
import LeaveCarryForward from "../../../components/modelpopup/LeaveCarryForward";
import LeaveConfigModal from "./LeaveConfigModal";
import Loader from "../../../components/Loader";
import AcknowledgmentPopup from "../../../utils/AcknowledgmentPopup";
import Breadcrumbs from "../../../components/Breadcrumbs";
import api from "../../../api/axios";
import Swal from "sweetalert2";

const CompanyLeaves = () => {
  const company_id = companyID();
  const user = localStorage.getItem("user");
  let department_id;
  if (user) {
    const companyDetails = JSON.parse(user);
    department_id = companyDetails.department_id;
  }

  const [CompanyLeaves, setCompanyLeaves] = useState([]);
  const [modalShow, setModalShow] = useState(false);
  const [configModalShow, setConfigModalShow] = useState(false);
  const [selectedLeave, setSelectedLeave] = useState(null);
  const [editingLeaveConfig, setEditingLeaveConfig] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [leaveTypesMaster, setLeaveTypesMaster] = useState([]);
  const [loading, setLoading] = useState({
    show: false,
    message: "",
  });
  const [acknowledgmentPopup, setAcknowledgmentPopup] = useState({
    show: false,
    status: null,
    message: null,
    icon: null,
  });

  useEffect(() => {
    if (company_id && department_id) {
      fetchCompanyLeaves();
      fetchDepartmentEmployees();
    }
  }, [company_id, department_id]);

  // Add separate useEffect to fetch leave types master after company leaves are loaded
  useEffect(() => {
    if (CompanyLeaves.length >= 0) {
      fetchLeaveTypesMaster();
    }
  }, [CompanyLeaves]);

  const fetchCompanyLeaves = () => {
    api
      .get(`leave-types?company_id=${company_id}`)
      .then((res) => {
        setCompanyLeaves(res.data);
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const fetchDepartmentEmployees = () => {
    api
      .get(
        `employees/employeeDetails/${company_id}?department_id=${department_id}`
      )
      .then((res) => {
        setEmployees(res.data);
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const fetchLeaveTypesMaster = () => {
    api
      .get("/leave-types-master")
      .then((res) => {
        // Handle the API response structure with 'result' property
        if (res.data.success && res.data.result) {
          // Filter out leave types that are already configured for this company
          const availableLeaveTypes = res.data.result.filter((masterLeave) =>
            !CompanyLeaves.some(
              (companyLeave) => companyLeave.leave_id === masterLeave.id
            )
          );
          setLeaveTypesMaster(availableLeaveTypes);
        } else {
          setLeaveTypesMaster([]);
        }
      })
      .catch((err) => {
        console.log("Error fetching leave types master:", err);
        setLeaveTypesMaster([]);
      });
  };

  const handleDeleteLeaveConfig = async (record) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "Do you want to delete this leave configuration? This action cannot be undone!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    });

    if (result.isConfirmed) {
      setLoading({ message: "Deleting leave configuration...", show: true });

      try {
        const response = await api.delete(`/leave-config/${record.id}`);

        if (response.data.success) {
          setAcknowledgmentPopup({
            show: true,
            message: "Leave configuration deleted successfully",
            status: "success",
            icon: "fa-regular fa-circle-check",
          });
          fetchCompanyLeaves(); // Refresh the list
        } else {
          setAcknowledgmentPopup({
            show: true,
            status: "danger",
            message:
              response.data.message ||
              "Failed to delete leave configuration",
            icon: "fa-regular fa-circle-xmark",
          });
        }
      } catch (error) {
        console.error("Error deleting leave configuration:", error);
        setAcknowledgmentPopup({
          show: true,
          status: "danger",
          message: "Something went wrong while deleting",
          icon: "fa-regular fa-circle-xmark",
        });
      } finally {
        setLoading({ message: "", show: false });
        hideAcknowledgmentPopup();
      }
    }
  };

  const columns = [
    {
      title: "Leave Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Total Days",
      dataIndex: "total_days",
      key: "total_days",
    },
    {
      title: "Allow leave forward",
      dataIndex: "leave_forwarded",
      key: "leave_forwarded",
    },
    {
      title: "Monthly Limit",
      dataIndex: "monthly_allow_leaves",
      key: "monthly_allow_leaves",
      render: (value) => value || "No limit",
    },
    {
      title: "Carry Forward",
      key: "carry_forward",
      render: (_, record) =>
        record.carry_forward ? (
          <Button
            type="primary"
            size="small"
            onClick={() => handleCarryForward(record)}
            className="bg-success"
          >
            Carry Forward
          </Button>
        ) : (
          <span>No</span>
        ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <div className="btn-group">
          <button
            className="btn btn-outline-primary btn-sm"
            onClick={() => handleEditLeaveConfig(record)}
            title="Edit Leave Config"
          >
            <i className="fa fa-edit" />
          </button>
          <button
            className="btn btn-outline-danger btn-sm"
            onClick={() => handleDeleteLeaveConfig(record)}
            title="Delete Leave Config"
          >
            <i className="fa fa-trash" />
          </button>
        </div>
      ),
    },
  ];

  const handleCarryForward = (record) => {
    setSelectedLeave(record);
    setModalShow(true);
  };

  const handleAddLeaveConfig = () => {
    setEditingLeaveConfig(null);
    setConfigModalShow(true);
  };

  const handleEditLeaveConfig = (record) => {
    setEditingLeaveConfig(record);
    setConfigModalShow(true);
  };

  const hideAcknowledgmentPopup = () => {
    const resetAcknowledgmentPopupData = () => {
      setAcknowledgmentPopup({
        show: false,
        status: null,
        message: null,
        icon: null,
      });
    };
    setTimeout(() => resetAcknowledgmentPopupData(), 2000);
  };

  const handleModalSubmit = (data) => {
    const currentYear = new Date().getFullYear();

    const carryForwardData = {
      leave_id: selectedLeave.leave_id,
      total_days: selectedLeave.total_days,
      leave_forwarded: selectedLeave.leave_forwarded,
      company_id: company_id,
      employee_ids: data.selectedEmployees.map((employee) => employee.value),
      carry_forward_year: currentYear,
    };
    setLoading({ message: "Leave carry forwarding...", show: true });
    api
      .post(`carry-forward-leaves`, carryForwardData)
      .then((res) => {
        const skippedEmployee = employees
          .filter((employee) =>
            res.data?.skippedEmployeeIds?.includes(employee.id)
          )
          .map((item) =>
            [item.first_name, item.last_name, item.middle_name]
              .filter(Boolean)
              .join(" ")
          )
          .join(", ");
        setAcknowledgmentPopup({
          show: true,
          message:
            res.data.message +
            ". " +
            (res.data?.skippedEmployeeIds?.length
              ? skippedEmployee + " Already leaves carry forwarded"
              : ""),
          status: "success",
          icon: "fa-regular fa-circle-check",
        });
      })
      .catch((err) => {
        console.log(err);
        setAcknowledgmentPopup({
          show: true,
          status: "danger",
          message: "Something went wrong",
          icon: "fa-regular fa-circle-xmark",
        });
      })
      .finally(() => {
        setLoading({ message: "", show: false });
        hideAcknowledgmentPopup();
      });
  };

  const handleConfigModalSubmit = (values) => {
    console.log("Save config values:", values);
    setConfigModalShow(false);
    setEditingLeaveConfig(null);

    // Refresh both company leaves and available leave types
    fetchCompanyLeaves();

    setAcknowledgmentPopup({
      show: true,
      message: `Leave configuration ${
        editingLeaveConfig ? "updated" : "created"
      } successfully`,
      status: "success",
      icon: "fa-regular fa-circle-check",
    });
    hideAcknowledgmentPopup();
  };

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        <Breadcrumbs
          maintitle="Company Leaves"
          title="Dashboard"
          subtitle="Company Leaves"
        />
        <div className="container mt-4">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h4>Leave Configurations</h4>
            <button
              className="btn btn-primary"
              onClick={handleAddLeaveConfig}
            >
              <i className="fa fa-plus me-1" />
              Add Leave Configuration
            </button>
          </div>

          <Table
            columns={columns}
            dataSource={CompanyLeaves}
            rowKey="id"
            bordered
            pagination={{ pageSize: 5 }}
          />

          {modalShow && (
            <LeaveCarryForward
              show={modalShow}
              handleClose={() => setModalShow(false)}
              handleSubmit={handleModalSubmit}
              employees={employees}
              selectedLeave={selectedLeave}
            />
          )}

          <LeaveConfigModal
            visible={configModalShow}
            onClose={() => {
              setConfigModalShow(false);
              setEditingLeaveConfig(null);
            }}
            onSubmit={handleConfigModalSubmit}
            editingRecord={editingLeaveConfig}
            leaveTypesMaster={leaveTypesMaster}
            currentLeaveTypeName={editingLeaveConfig?.name}
          />
        </div>

        {acknowledgmentPopup.show ? (
          <AcknowledgmentPopup
            popupVariant={acknowledgmentPopup.status}
            popupMessage={acknowledgmentPopup.message}
            popupIcon={acknowledgmentPopup.icon}
          />
        ) : (
          <></>
        )}
        <Loader show={loading.show} loadingMessage={loading.message} />
      </div>
    </div>
  );
};

export default CompanyLeaves;
